import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchRoleMatrix } from "../../utils/apiUtils";
import { ROLE_LABELS, ROLE_OPTIONS } from "../../utils/roleOptions";
import { notesForRole, viewsForRole } from "../../utils/navAccess";
import AccessTabs from "./AccessTabs";
import "./Access.scss";

const RoleAccess = () => {
    const [selectedRole, setSelectedRole] = useState("admin");
    const { data, isLoading, error } = useQuery({
        queryKey: ["access-role-matrix"],
        queryFn: fetchRoleMatrix,
        retry: false,
    });

    const endpointsByRole = data?.endpointsByRole || {};
    const roles = useMemo(() => {
        const fromNav = ROLE_OPTIONS.map((r) => r.value);
        const fromApi = Object.keys(endpointsByRole);
        return [...new Set(["admin", ...fromNav, ...fromApi, "jv"])].sort((a, b) => a.localeCompare(b));
    }, [endpointsByRole]);

    const endpoints = selectedRole === "admin"
        ? ["* (admin bypasses path checks)"]
        : (endpointsByRole[selectedRole] || []);
    const views = viewsForRole(selectedRole);
    const notes = notesForRole(selectedRole, selectedRole === "admin" ? ["*"] : endpointsByRole[selectedRole]);

    return (
        <div className="container">
            <p className="title">Access Management</p>
            <p className="sub-title">Which roles can see which views and call which endpoints</p>
            <AccessTabs />

            <div className="access-management">
                <div className="details">
                    <div className="access-user-pane">
                        <div className="access-user-list">
                            <div className="access-pane-header">
                                <p className="access-management-title">Roles</p>
                            </div>
                            <ul className="scrollable access-user-scroll">
                                {roles.map((role) => (
                                    <li
                                        key={role}
                                        className={selectedRole === role ? "selected" : ""}
                                        onClick={() => setSelectedRole(role)}
                                    >
                                        <div className="user-info">
                                            <div>
                                                <p className="user-name">{ROLE_LABELS[role] || role}</p>
                                                <p className="sub-title">{role}</p>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="access-role-detail scrollable-form">
                            {isLoading ? (
                                <div className="loading" style={{ height: "3rem", borderRadius: "0.4rem" }} />
                            ) : error ? (
                                <p className="danger">Could not load endpoint grants: {error.message}</p>
                            ) : (
                                <>
                                    <p className="access-role-heading">{ROLE_LABELS[selectedRole] || selectedRole}</p>
                                    <p className="sub-title">
                                        {views.length} view{views.length === 1 ? "" : "s"} · {endpoints.length} endpoint pattern{endpoints.length === 1 ? "" : "s"}
                                    </p>

                                    {notes.length > 0 && (
                                        <ul className="access-role-notes">
                                            {notes.map((note) => <li key={note}>{note}</li>)}
                                        </ul>
                                    )}

                                    <p className="access-role-section">Frontend views</p>
                                    {views.length === 0 ? (
                                        <p className="sub-title">None</p>
                                    ) : (
                                        <ul className="access-role-views">
                                            {views.map((view) => (
                                                <li key={view.to}>
                                                    <span className="access-role-view-label">{view.label}</span>
                                                    <span className="access-role-path">{view.to}</span>
                                                    {view.alwaysShow && <span className="role">everyone</span>}
                                                    {view.screens?.length > 0 && (
                                                        <p className="sub-title">{view.screens.join(" · ")}</p>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <p className="access-role-section">Backend endpoints</p>
                                    {selectedRole !== "admin" && data?.alwaysAllowedPaths?.length > 0 && (
                                        <p className="sub-title" style={{ marginBottom: "0.6rem" }}>
                                            Also always allowed for any signed-in user: {data.alwaysAllowedPaths.join(", ")}
                                        </p>
                                    )}
                                    {endpoints.length === 0 ? (
                                        <p className="sub-title">None</p>
                                    ) : (
                                        <div className="access-role-paths">
                                            {endpoints.map((path) => (
                                                <code key={path}>{path}</code>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoleAccess;
